import React, { useState, useEffect } from "react";
import axios from "axios";
import { Modal, Button, Form, Table, Row, Col, Badge } from "react-bootstrap";
import Swal from "sweetalert2";
import api from "../utils/api";

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [show, setShow] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    cost: "",
    mrp: "",
    category_id: "",
    sku: "",
    short_description: "",
    features: "",
    description: "",
    product_reviews: "",
    youtube_video_link: "",
    colors: [],
    images: [],
    image_3d: null,
    description_images: [],
    // Track deleted images for backend
    deleted_images: [],
    deleted_description_images: [],
    delete_image_3d: false
  });

  // Helper function to safely parse JSON or return default value
  const safeJsonParse = (str, defaultValue = []) => {
    try {
      if (typeof str === 'string') {
        return JSON.parse(str);
      }
      if (Array.isArray(str)) {
        return str;
      }
      return defaultValue;
    } catch (e) {
      console.error('JSON parse error:', e);
      return defaultValue;
    }
  };

  // Generate URL-friendly slug from text
  const generateSlug = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Remove multiple hyphens
      .trim('-');
  };

  // Generate product URL
  const getProductUrl = (slug) => {
    return `https://www.wonderplast.in/product/${slug}`;
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(api.getUrl("products.php"));
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(api.getUrl("categories.php"));
      if (response.data.success && Array.isArray(response.data.categories)) {
        setCategories(response.data.categories);
      } else {
        console.error("Unexpected response format:", response.data);
        setCategories([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    }
  };

  const handleShow = (product = null) => {
    console.log("handleShow called with product:", product);
    if (product) {
      console.log("Setting editing product:", product.name);
      setEditingProduct(product);
      const formDataObj = {
        name: product.name,
        slug: product.slug || "",
        cost: product.cost,
        mrp: product.mrp,
        category_id: product.category_id,
        sku: product.sku || "",
        short_description: product.short_description,
        features: product.features,
        description: product.description,
        product_reviews: product.product_reviews || "",
        youtube_video_link: product.youtube_video_link || "",
        colors: safeJsonParse(product.colors),
        images: [], // This is for new files to upload
        image_3d: null, // This is for new file to upload
        description_images: [], // This is for new files to upload
        // Add existing paths for display purposes
        existing_images: safeJsonParse(product.images),
        existing_description_images: safeJsonParse(product.description_images),
        existing_image_3d: product.image_3d,
        // Initialize deleted tracking
        deleted_images: [],
        deleted_description_images: [],
        delete_image_3d: false
      };
      console.log("Setting form data:", formDataObj);
      setFormData(formDataObj);
    } else {
      console.log("Creating new product");
      setEditingProduct(null);
      setFormData({
        name: "",
        slug: "",
        cost: "",
        mrp: "",
        category_id: "",
        sku: "",
        short_description: "",
        features: "",
        description: "",
        product_reviews: "",
        youtube_video_link: "",
        colors: [],
        images: [],
        image_3d: null,
        description_images: [],
        deleted_images: [],
        deleted_description_images: [],
        delete_image_3d: false
      });
    }
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    setEditingProduct(null);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    // Validate files immediately
    const errors = validateFiles(files);
    if (errors.length > 0) {
      Swal.fire("Validation Error!", errors.join("<br>"), "error");
      return;
    }
    setFormData({ ...formData, images: files });
  };

  const handleDescImagesChange = (e) => {
    const files = Array.from(e.target.files);
    // Validate files immediately
    const errors = validateFiles(files);
    if (errors.length > 0) {
      Swal.fire("Validation Error!", errors.join("<br>"), "error");
      return;
    }
    setFormData({ ...formData, description_images: files });
  };

  const handle3DFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file immediately with specific 3D model types
      const errors = validate3DModelFile(file, 200 * 1024 * 1024); // Max size 200MB
      if (errors.length > 0) {
        Swal.fire("Validation Error!", errors.join("<br>"), "error");
        return;
      }
      setFormData({ ...formData, image_3d: file });
    }
  };

  const handleColorChange = (e, index) => {
    const newColors = [...formData.colors];
    newColors[index] = e.target.value;
    setFormData({ ...formData, colors: newColors });
  };

  const addColorField = () => {
    setFormData({ ...formData, colors: [...formData.colors, ""] });
  };

  const removeColorField = (index) => {
    const newColors = formData.colors.filter((_, i) => i !== index);
    setFormData({ ...formData, colors: newColors });
  };

  const validateFiles = (files, maxSize = 5 * 1024 * 1024, allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']) => {
    const errors = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Check file size
      if (file.size > maxSize) {
        errors.push(`${file.name} is too large. Max size is ${maxSize / (1024 * 1024)}MB.`);
        continue;
      }
      
      // Check file type
      if (!allowedTypes.includes(file.type)) {
        errors.push(`${file.name} is not a valid image type. Allowed: JPG, PNG, GIF.`);
      }
    }
    
    return errors;
  };

  const validate3DModelFile = (file, maxSize = 200 * 1024 * 1024) => { // Default to 200MB
    const errors = [];
    
    // Check file size
    if (file.size > maxSize) {
      errors.push(`${file.name} is too large. Max size is ${maxSize / (1024 * 1024)}MB.`);
    }

    // Check file extension
    const allowedExtensions = ['.glb', '.gltf', '.fbx'];
    const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
    if (!allowedExtensions.includes(fileExtension)) {
      errors.push(`${file.name} is not a valid 3D model type. Allowed: GLB, GLTF, FBX.`);
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate image files if any are uploaded
    const imageErrors = validateFiles(formData.images);
    const descImageErrors = validateFiles(formData.description_images);
    
    if (imageErrors.length > 0 || descImageErrors.length > 0) {
      Swal.fire("Validation Error!", [...imageErrors, ...descImageErrors].join("<br>"), "error");
      return;
    }

    // Validate 3D model file if uploaded
    if (formData.image_3d) {
      const modelErrors = validate3DModelFile(formData.image_3d, 200 * 1024 * 1024); // Max size 200MB
      if (modelErrors.length > 0) {
        Swal.fire("Validation Error!", modelErrors.join("<br>"), "error");
        return;
      }
    }

    const productData = new FormData();
    productData.append("product_name", formData.name);
    productData.append("slug", formData.slug);
    productData.append("cost", formData.cost);
    productData.append("mrp", formData.mrp);
    productData.append("category_id", formData.category_id);
    productData.append("sku", formData.sku);
    productData.append("short_description", formData.short_description);
    productData.append("features", formData.features);
    productData.append("description", formData.description);
    productData.append("product_reviews", formData.product_reviews);
    productData.append("youtube_video_link", formData.youtube_video_link);
    productData.append("colors", JSON.stringify(formData.colors));
    
    // Debug logging
    console.log("Frontend Debug - FormData name:", formData.name);
    console.log("Frontend Debug - FormData entries count:", productData.entries().length);

    // Append images with proper field names
    if (formData.images && formData.images.length > 0) {
      formData.images.forEach((file) => productData.append("images[]", file));
    }
    
    // Append description images with proper field names
    if (formData.description_images && formData.description_images.length > 0) {
      formData.description_images.forEach((file) =>
        productData.append("description_images[]", file)
      );
    }

    // Append 3D image if exists
    if (formData.image_3d) {
      productData.append("image_3d", formData.image_3d);
    }

    try {
      const url = api.getUrl("products.php");
      const config = {
        headers: { "Content-Type": "multipart/form-data" }
      };

      if (editingProduct) {
        // Send existing image paths to backend
        if (formData.existing_images && formData.existing_images.length > 0) {
          productData.append("existing_images", JSON.stringify(formData.existing_images));
        }
        if (formData.existing_description_images && formData.existing_description_images.length > 0) {
          productData.append("existing_description_images", JSON.stringify(formData.existing_description_images));
        }
        if (formData.existing_image_3d) {
          productData.append("existing_image_3d", formData.existing_image_3d);
        }

        // Send deleted images to backend
        if (formData.deleted_images && formData.deleted_images.length > 0) {
          productData.append("deleted_images", JSON.stringify(formData.deleted_images));
        }
        if (formData.deleted_description_images && formData.deleted_description_images.length > 0) {
          productData.append("deleted_description_images", JSON.stringify(formData.deleted_description_images));
        }
        if (formData.delete_image_3d) {
          productData.append("delete_image_3d", "true");
        }

        productData.append("_method", "PUT");
        productData.append("id", editingProduct.id);

        await axios.post(url, productData, config);
        Swal.fire("Success!", "Product updated successfully.", "success");
      } else {
        await axios.post(url, productData, config);
        Swal.fire("Success!", "Product added successfully.", "success");
      }
      
      fetchProducts();
      handleClose();
    } catch (error) {
      console.error("Error saving product:", error);
      const errorMessage = error.response?.data?.message || error.response?.data || "Failed to save product.";
      Swal.fire("Error!", typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage), "error");
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This product will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ff6b00",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(api.getUrl("products.php"), {
            data: { id }
          });
          Swal.fire("Deleted!", "The product has been removed.", "success");
          fetchProducts();
        } catch (error) {
          Swal.fire("Error!", "Failed to delete product.", "error");
        }
      }
    });
  };

  const getImageUrl = (path) => {
    if (!path) return '';
    // Handle both string and array inputs
    const imagePath = Array.isArray(path) ? path[0] : path;
    // Check if imagePath exists and is a string before calling replace
    if (typeof imagePath !== 'string') return '';
    // Normalize path and construct full URL
    return api.getImageUrl(imagePath);
  };

  return (
    <div className="products-container">
      <h2>Manage Products</h2>
      <Button variant="primary" onClick={() => handleShow()}>Add Product</Button>
      
      <Table striped bordered hover className="mt-3">
        <thead>
          <tr>
            <th>Name</th>
            <th>Slug</th>
            <th>SKU</th>
            <th>Price</th>
            <th>Category</th>
            <th>Colors</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product) => (
            <tr key={product.id}>
              <td>
                
                    <strong>{product.name}</strong>
                
              </td>
              <td>
                <code className="slug-display">
                  {product.slug || '-'}
                </code>
              </td>
              <td>{product.sku || '-'}</td>
              <td>
                <div>Cost: Rs.{product.cost}</div>
                <div>MRP: Rs.{product.mrp}</div>
              </td>
              <td>{product.category_name}</td>
              <td>
                {safeJsonParse(product.colors).map((color, index) => (
                  <Badge key={index} className="me-1" bg="secondary">
                    {color}
                  </Badge>
                ))}
              </td>
              <td>
                <Button variant="warning" size="sm" onClick={() => handleShow(product)}>
                  Edit
                </Button>{' '}
                <Button variant="danger" size="sm" onClick={() => handleDelete(product.id)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Add/Edit Product Modal */}
      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingProduct ? "Edit Product" : "Add Product"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name*</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      console.log("Name field changed:", e.target.value);
                      setFormData(prevFormData => {
                        const newName = e.target.value;
                        const newSlug = generateSlug(newName);
                        return { ...prevFormData, name: newName, slug: newSlug };
                      });
                    }}
                    required
                  />
                  <Form.Text className="text-muted">
                    Current value: {formData.name}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>URL Slug*</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.slug || ''}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="e.g., modern-office-chair"
                    required
                  />
                  <Form.Text className="text-muted">
                    URL-friendly version of the product name
                  </Form.Text>
                  
                  {formData.slug && (
                    <div className="mt-2">
                      <small className="text-muted">Preview URL:</small>
                      <div className="d-flex align-items-center mt-1">
                        <code className="url-preview flex-grow-1">
                          {getProductUrl(formData.slug)}
                        </code>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(getProductUrl(formData.slug));
                            Swal.fire({
                              icon: 'success',
                              title: 'Copied!',
                              text: 'URL copied to clipboard',
                              timer: 2000,
                              showConfirmButton: false
                            });
                          }}
                        >
                          Copy
                        </Button>
                      </div>
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>SKU</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    placeholder="Product SKU or code"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Short Description*</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.short_description}
                    onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Features</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.features}
                    onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Description*</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cost*</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>MRP*</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={formData.mrp}
                    onChange={(e) => setFormData({ ...formData, mrp: e.target.value })}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Category*</Form.Label>
                  <Form.Select
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.title}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Product Reviews</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={formData.product_reviews}
                    onChange={(e) => setFormData({ ...formData, product_reviews: e.target.value })}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>YouTube Video Link</Form.Label>
                  <Form.Control
                    type="url"
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={formData.youtube_video_link}
                    onChange={(e) => setFormData({ ...formData, youtube_video_link: e.target.value })}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Colors</Form.Label>
                  {formData.colors.map((color, index) => (
                    <div key={index} className="d-flex mb-2">
                      <Form.Control
                        type="text"
                        value={color}
                        onChange={(e) => handleColorChange(e, index)}
                        placeholder="Color name or hex code"
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        className="ms-2"
                        onClick={() => removeColorField(index)}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline-secondary" size="sm" onClick={addColorField}>
                    + Add Color
                  </Button>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Main Images* (Upto 5)</Form.Label>
                  <Form.Control
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileChange}
                    max="5"
                  />
                  <Form.Text className="text-muted">
                    Max 5 images. Max size: 5MB per image. Allowed formats: JPG, PNG, GIF.
                  </Form.Text>
                  {formData.images && formData.images.length > 0 && (
                    <div className="mt-2">
                      <small>Selected Images:</small>
                      <div className="d-flex flex-wrap gap-2 mt-1">
                        {formData.images.map((file, i) => (
                          <div key={i} className="text-center">
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`Selected ${i}`}
                              style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                            />
                            <div className="small">{file.name}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {editingProduct && formData.existing_images && formData.existing_images.length > 0 && (
                    <div className="mt-2">
                      <small>Current Images:</small>
                      <div className="d-flex flex-wrap gap-2 mt-1">
                        {formData.existing_images.map((img, i) => (
                          <div key={i} className="text-center position-relative">
                            <img
                              src={getImageUrl(img)}
                              alt={`Product ${i}`}
                              style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <Button
                              variant="danger"
                              size="sm"
                              className="position-absolute top-0 end-0 p-0"
                              style={{ width: '20px', height: '20px', fontSize: '12px' }}
                              onClick={() => {
                                const deletedImage = formData.existing_images[i];
                                const newImages = formData.existing_images.filter((_, index) => index !== i);
                                setFormData({
                                  ...formData,
                                  existing_images: newImages,
                                  deleted_images: [...formData.deleted_images, deletedImage]
                                });
                              }}
                            >
                              ×
                            </Button>
                          </div>
                        ))}
                      </div>
                      <small className="text-muted">Click × to remove an image. These images will be deleted when you save.</small>
                    </div>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Description Images (Max 4 Images)</Form.Label>
                  <Form.Control
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleDescImagesChange}
                    max="4"
                  />
                  <Form.Text className="text-muted">
                    Max 4 images. Max size: 5MB per image. Allowed formats: JPG, PNG, GIF.
                  </Form.Text>
                  {formData.description_images && formData.description_images.length > 0 && (
                    <div className="mt-2">
                      <small>Selected Images:</small>
                      <div className="d-flex flex-wrap gap-2 mt-1">
                        {formData.description_images.map((file, i) => (
                          <div key={i} className="text-center">
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`Selected ${i}`}
                              style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                            />
                            <div className="small">{file.name}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {editingProduct && formData.existing_description_images && formData.existing_description_images.length > 0 && (
                    <div className="mt-2">
                      <small>Current Description Images:</small>
                      <div className="d-flex flex-wrap gap-2 mt-1">
                        {formData.existing_description_images.map((img, i) => (
                          <div key={i} className="text-center position-relative">
                            <img
                              src={getImageUrl(img)}
                              alt={`Desc ${i}`}
                              style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <Button
                              variant="danger"
                              size="sm"
                              className="position-absolute top-0 end-0 p-0"
                              style={{ width: '20px', height: '20px', fontSize: '12px' }}
                              onClick={() => {
                                const deletedImage = formData.existing_description_images[i];
                                const newImages = formData.existing_description_images.filter((_, index) => index !== i);
                                setFormData({
                                  ...formData,
                                  existing_description_images: newImages,
                                  deleted_description_images: [...formData.deleted_description_images, deletedImage]
                                });
                              }}
                            >
                              ×
                            </Button>
                          </div>
                        ))}
                      </div>
                      <small className="text-muted">Click × to remove an image. These images will be deleted when you save.</small>
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>3D Model (GLB/GLTF/FBX)</Form.Label>
              <Form.Control
                type="file"
                accept=".glb,.gltf,.fbx"
                onChange={handle3DFileChange}
              />
              {editingProduct && formData.existing_image_3d && (
                <div className="mt-2 d-flex align-items-center">
                  <small>Current 3D Model:</small>
                  <div className="ms-2">{formData.existing_image_3d}</div>
                  <Button
                    variant="danger"
                    size="sm"
                    className="ms-2"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        existing_image_3d: null,
                        delete_image_3d: true
                      });
                    }}
                  >
                    Remove
                  </Button>
                </div>
              )}
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button variant="secondary" onClick={handleClose} className="me-2">
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editingProduct ? "Update" : "Save"} Product
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Products;
